import { list } from "@vercel/blob";

/**
 * Optimized Vercel Blob Service implementing best practices
 * - Treats blobs as immutable objects
 * - Implements proper caching strategies
 * - Minimizes API calls
 * - Uses React cache patterns
 */

class OptimizedVercelBlobService {
  constructor() {
    this.storeId = 'store_Tz896OQOYcnpeXq2';
    this.storePath = 'gifted-solutions-images';
    
    // In-memory cache for blob URLs (treating blobs as immutable)
    this.blobCache = new Map();
    this.cacheExpiry = 60 * 60 * 1000; // 1 hour cache
    this.lastFetch = null;
    this.cachedBlobs = null;
    
    console.log(`🚀 OptimizedVercelBlobService initialized for store: ${this.storeId}`);
  }

  /**
   * Get cached blob data or fetch if expired
   * Implements immutable blob best practice
   */
  async getCachedBlobs() {
    const now = Date.now();
    
    // Return cached data if still valid
    if (this.cachedBlobs && this.lastFetch && (now - this.lastFetch) < this.cacheExpiry) {
      console.log('📋 Using cached blob data');
      return this.cachedBlobs;
    }

    console.log('🔄 Fetching fresh blob data from Vercel');
    
    try {
      // Check if Vercel Blob is available
      if (!list) {
        console.warn('⚠️ Vercel Blob list function not available');
        return this.cachedBlobs || [];
      }

      const { blobs } = await list({
        prefix: `${this.storePath}/`,
        limit: 1000
      });

      // Process and cache the blobs
      const processedBlobs = blobs.map(blob => ({
        filename: blob.pathname.replace(`${this.storePath}/`, ''),
        url: blob.url,
        downloadUrl: blob.downloadUrl,
        pathname: blob.pathname,
        size: blob.size,
        uploadedAt: blob.uploadedAt,
        storeId: this.storeId,
        // Add cache metadata
        cachedAt: now,
        immutableUrl: blob.url // Treat as immutable
      }));

      // Cache the results
      this.cachedBlobs = processedBlobs;
      this.lastFetch = now;
      
      console.log(`✅ Cached ${processedBlobs.length} blobs from Vercel`);
      return processedBlobs;
      
    } catch (error) {
      console.error('❌ Error fetching blobs from Vercel:', error);
      
      // Return cached data if available, even if expired
      if (this.cachedBlobs) {
        console.log('⚠️ Using expired cache due to fetch error');
        return this.cachedBlobs;
      }
      
      return [];
    }
  }

  /**
   * Get all product images with optimized caching
   * Implements Vercel Blob best practices
   */
  async getProductImages() {
    try {
      const blobs = await this.getCachedBlobs();
      
      // Filter for image files only
      const imageBlobs = blobs.filter(blob => {
        const filename = blob.filename.toLowerCase();
        return filename.endsWith('.jpg') || 
               filename.endsWith('.jpeg') || 
               filename.endsWith('.png') || 
               filename.endsWith('.gif') || 
               filename.endsWith('.webp') ||
               filename.endsWith('.avif');
      });

      console.log(`🖼️ Filtered ${imageBlobs.length} image files from ${blobs.length} total blobs`);
      return imageBlobs;
      
    } catch (error) {
      console.error('❌ Error getting product images:', error);
      return [];
    }
  }

  /**
   * Get images with metadata integration
   * Optimized for React components
   */
  async getImagesWithMetadata() {
    try {
      const images = await this.getProductImages();
      
      if (images.length === 0) {
        return [];
      }

      // Load metadata service dynamically to avoid circular imports
      const { default: firebaseMetadataService } = await import('./firebaseMetadataService');
      
      // Process images with metadata
      const imagesWithMetadata = await Promise.all(images.map(async (image) => {
        let metadata = null;
        
        try {
          // Use cached metadata if available
          const cacheKey = `metadata_${image.filename}`;
          if (this.blobCache.has(cacheKey)) {
            const cached = this.blobCache.get(cacheKey);
            if (Date.now() - cached.timestamp < this.cacheExpiry) {
              metadata = cached.data;
            }
          }
          
          // Fetch metadata if not cached
          if (!metadata) {
            metadata = await firebaseMetadataService.getImageMetadata(image.filename);
            
            // Cache the metadata
            this.blobCache.set(cacheKey, {
              data: metadata,
              timestamp: Date.now()
            });
          }
        } catch (error) {
          console.log(`No metadata found for ${image.filename}`);
        }

        return {
          id: image.filename.replace(/\.[^/.]+$/, ""),
          name: image.filename,
          url: image.immutableUrl, // Use immutable URL
          publicUrl: image.immutableUrl,
          downloadUrl: image.downloadUrl,
          fullPath: image.pathname,
          storage: 'vercel-blob',
          storageProvider: 'vercel-blob',
          size: image.size,
          uploadedAt: image.uploadedAt,
          
          // Metadata from Firebase
          title: metadata?.title || '',
          description: metadata?.description || '',
          category: metadata?.category || '',
          price: metadata?.price || null,
          currency: metadata?.currency || 'K',
          inStock: metadata?.in_stock !== undefined ? metadata.in_stock : false,
          featured: metadata?.featured || false,
          hasMetadata: !!metadata,
          
          // Vercel Blob specific
          vercel_blob_url: image.immutableUrl,
          vercel_blob_pathname: image.pathname,
          vercel_blob_size: image.size,
          vercel_blob_uploaded_at: image.uploadedAt,
          
          // Cache info
          cachedAt: image.cachedAt,
          isFromCache: !!this.lastFetch
        };
      }));

      return imagesWithMetadata;
      
    } catch (error) {
      console.error('❌ Error getting images with metadata:', error);
      return [];
    }
  }

  /**
   * Preload images for better performance
   * Implements browser caching optimization
   */
  preloadImages(imageUrls) {
    if (!Array.isArray(imageUrls)) return;
    
    imageUrls.forEach(url => {
      if (url && typeof url === 'string') {
        const img = new Image();
        img.src = url;
        // Browser will cache the image
      }
    });
    
    console.log(`🚀 Preloaded ${imageUrls.length} images for browser caching`);
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      blobCacheSize: this.blobCache.size,
      lastFetch: this.lastFetch,
      cacheAge: this.lastFetch ? Date.now() - this.lastFetch : null,
      cacheExpiry: this.cacheExpiry,
      isExpired: this.lastFetch ? (Date.now() - this.lastFetch) > this.cacheExpiry : true,
      cachedBlobsCount: this.cachedBlobs ? this.cachedBlobs.length : 0
    };
  }

  /**
   * Clear cache manually
   */
  clearCache() {
    this.blobCache.clear();
    this.cachedBlobs = null;
    this.lastFetch = null;
    console.log('🧹 Cache cleared');
  }

  /**
   * Force refresh cache
   */
  async refreshCache() {
    this.clearCache();
    return await this.getCachedBlobs();
  }

  /**
   * Get store information with caching
   */
  async getStoreInfo() {
    try {
      const blobs = await this.getCachedBlobs();
      const images = blobs.filter(blob => {
        const filename = blob.filename.toLowerCase();
        return filename.endsWith('.jpg') || filename.endsWith('.jpeg') || 
               filename.endsWith('.png') || filename.endsWith('.gif') || 
               filename.endsWith('.webp') || filename.endsWith('.avif');
      });

      const totalSize = blobs.reduce((sum, blob) => sum + (blob.size || 0), 0);
      
      const fileTypes = {};
      blobs.forEach(blob => {
        const ext = blob.filename.split('.').pop()?.toLowerCase() || 'unknown';
        fileTypes[ext] = (fileTypes[ext] || 0) + 1;
      });

      const cacheStats = this.getCacheStats();

      return {
        success: true,
        storeId: this.storeId,
        storePath: this.storePath,
        totalFiles: blobs.length,
        totalSize: totalSize,
        imageFiles: images.length,
        fileTypes: fileTypes,
        cacheStats: cacheStats,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ Error getting store info:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Create and export singleton instance
const optimizedVercelBlobService = new OptimizedVercelBlobService();
export default optimizedVercelBlobService;
